// What a place moves: parts of the world and the underlyings most exposed to them, with one line on
// why. The `REGIONS` table of atlas-geo `exposure.rs`, same order and values; syms come out under
// their canonical names (BRENTOIL is BZ, GOLD is XAU, XYZ100 and QQQ are NDX).
//
// Added here: a United Kingdom region for sterling (xyz lists GBP; atlas had none, so London fell in
// the euro area).

import type { RegionRef } from "./types.ts";
import { canonical } from "./symbols.ts";

export type Region = {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /** How far from the centre still counts as this place, km. */
  km: number;
  /** Underlyings, most exposed first, canonical. */
  syms: readonly string[];
  why: string;
  /** What to search event markets for about it, when not its name. */
  q: string | null;
};

type Row = readonly [id: string, name: string, lat: number, lon: number, km: number, syms: readonly string[], why: string, q: string | null];

/** Smallest first where regions nest (Qatar in the Gulf, Hormuz beside Iran): the closest fit wins. */
const ROWS: readonly Row[] = [
  // energy: chokepoints
  ["hormuz", "Strait of Hormuz", 26.57, 56.25, 260, ["BRENTOIL", "CL", "BZ"], "About a fifth of the world's oil passes through the strait.", "Iran"],
  ["bab-el-mandeb", "Red Sea", 15.5, 41.8, 750, ["BRENTOIL", "CL", "BZ"], "The Suez route for tankers, and the Houthis' target.", "Houthi"],
  ["suez", "Suez Canal", 30.6, 32.34, 220, ["BRENTOIL", "CL", "BZ"], "Europe's tanker and container route from the Gulf.", "Egypt"],
  ["panama", "Panama Canal", 9.1, -79.7, 220, ["CL", "NATGAS"], "US crude and LNG exports to Asia pass here.", "Panama"],
  // energy: producers
  ["qatar", "Qatar", 25.3, 51.2, 180, ["NATGAS", "BRENTOIL"], "One of the largest LNG exporters.", null],
  ["persian-gulf", "Persian Gulf", 27.3, 51.0, 650, ["BRENTOIL", "CL", "NATGAS"], "The Gulf producers, and Qatar's gas.", "Iran"],
  ["iran", "Iran", 32.4, 53.7, 950, ["BRENTOIL", "CL", "GOLD", "XAU"], "An OPEC producer on the Strait of Hormuz: crude carries its risk, gold its escalation.", null],
  ["levant", "Israel and the Levant", 32.3, 35.2, 330, ["BRENTOIL", "CL", "GOLD", "XAU"], "Conflict here puts a risk premium in crude and a bid in gold.", "Israel"],
  ["iraq", "Iraq", 33.2, 43.7, 560, ["BRENTOIL", "CL"], "OPEC's second producer.", null],
  ["saudi", "Saudi Arabia", 24.0, 45.0, 1100, ["BRENTOIL", "CL"], "OPEC's largest producer and its swing supply.", null],
  ["libya", "Libya", 27.0, 17.0, 850, ["BRENTOIL", "CL"], "Its output swings with the fighting over the fields.", null],
  ["nigeria", "Nigeria", 9.1, 8.7, 650, ["BRENTOIL", "CL"], "Africa's largest oil producer, prone to outages.", null],
  ["venezuela", "Venezuela", 7.1, -66.2, 650, ["CL", "BRENTOIL"], "The largest proven reserves, and sanctions on them.", null],
  ["north-sea", "North Sea", 57.5, 2.5, 520, ["BRENTOIL", "BZ", "NATGAS"], "Brent's own basin, and Norway's gas to Europe.", "Norway"],
  ["ukraine", "Ukraine", 49.0, 31.4, 650, ["NATGAS", "GOLD", "XAU"], "The war on Europe's gas routes.", null],
  ["norilsk", "Norilsk", 69.35, 88.2, 450, ["PALLADIUM", "XPD"], "Nornickel mines most of the world's palladium here.", "Russia"],
  ["west-siberia", "West Siberia", 61.0, 73.0, 1300, ["BRENTOIL", "CL", "NATGAS"], "Russia's oil and gas heartland.", "Russia"],
  ["russia", "Russia", 56.0, 45.0, 1500, ["NATGAS", "BRENTOIL", "PALLADIUM"], "Sanctions and supply cuts move gas, crude and palladium.", null],
  ["us-gulf", "US Gulf Coast", 28.8, -92.0, 650, ["NATGAS", "CL"], "Refineries, LNG export terminals and offshore rigs, in hurricane country.", "hurricane"],
  ["permian", "Permian Basin", 31.8, -102.5, 380, ["CL"], "The largest US oil field.", "oil"],
  ["alberta", "Alberta oil sands", 57.0, -111.5, 520, ["CL"], "Canada's heavy crude, most of it sold to the US.", "Canada"],
  // metals
  ["peru", "Peru", -11.5, -75.5, 850, ["COPPER", "XCU"], "The second copper producer after Chile.", null],
  ["chile", "Chile", -26.5, -70.2, 1100, ["COPPER", "XCU"], "The largest copper producer: Escondida, Chuquicamata, El Teniente.", null],
  ["copperbelt", "Copperbelt", -11.7, 27.5, 600, ["COPPER", "XCU"], "Congo and Zambia's copper and cobalt.", "Congo"],
  ["south-africa", "South Africa", -26.2, 28.0, 750, ["PLATINUM", "XPT", "PALLADIUM", "GOLD"], "Most of the world's platinum, from the Bushveld.", null],
  ["kazakhstan", "Kazakhstan", 48.0, 67.0, 1100, ["URNM"], "The largest uranium producer.", null],
  ["niger", "Niger", 17.6, 8.1, 800, ["URNM"], "A uranium supplier to Europe, and a coup away from it.", null],
  // chips and companies
  ["taiwan", "Taiwan", 23.7, 121.0, 330, ["TSM", "NVDA", "ASML"], "TSMC makes most of the world's leading-edge chips.", null],
  ["korea", "South Korea", 36.5, 127.8, 380, ["SAMSUNG", "SKHYNIX", "SMSN"], "Samsung and SK hynix: the world's memory chips.", null],
  ["shenzhen", "Shenzhen and Hong Kong", 22.45, 114.1, 160, ["TENCENT", "XIAOMI", "BABA"], "China's listed tech.", "China"],
  ["japan", "Japan", 36.2, 138.3, 900, ["JPY"], "The yen: the Bank of Japan and the carry trade.", null],
  ["china", "China", 34.0, 108.0, 1800, ["BABA", "TENCENT", "COPPER", "XCU"], "China's tech, and half the world's copper demand.", null],
  ["netherlands", "Netherlands", 52.2, 5.3, 190, ["ASML"], "ASML: the only maker of EUV lithography machines.", null],
  // added: sterling
  ["uk", "United Kingdom", 54.0, -2.0, 600, ["GBP"], "Sterling: the Bank of England and the gilt market.", "Bank of England"],
  ["euro-area", "Euro area", 48.8, 9.0, 1100, ["EUR"], "The euro: the ECB and Europe's growth.", "ECB"],
  ["bay-area", "Silicon Valley", 37.45, -122.1, 140, ["NVDA", "AAPL", "GOOGL", "META"], "Where the largest tech companies are.", "Nvidia"],
  ["seattle", "Seattle", 47.6, -122.3, 110, ["MSFT", "AMZN"], "Microsoft and Amazon.", "Microsoft"],
  ["new-york", "New York", 40.7, -74.0, 160, ["SP500", "XYZ100", "SPY", "QQQ"], "Wall Street: US equities.", "S&P 500"],
  ["us", "United States", 39.5, -98.5, 2300, ["SP500", "XYZ100", "SPY", "QQQ"], "US equity indices.", "Fed"],
];

function canonicalSyms(syms: readonly string[]): string[] {
  const out: string[] = [];
  for (const sym of syms) {
    const canon = canonical(sym);
    if (!out.includes(canon)) out.push(canon);
  }
  return out;
}

export const REGIONS: readonly Region[] = ROWS.map(([id, name, lat, lon, km, syms, why, q]) => ({
  id,
  name,
  lat,
  lon,
  km,
  syms: canonicalSyms(syms),
  why,
  q,
}));

export const REGION_BY_ID: ReadonlyMap<string, Region> = new Map(REGIONS.map((r) => [r.id, r]));

/** The engine's sphere (atlas `EARTH_RADIUS_KM`). */
const EARTH_RADIUS_KM = 6371.0088;
const DEG = Math.PI / 180;

/** Haversine distance, km. */
export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const p1 = lat1 * DEG;
  const p2 = lat2 * DEG;
  const dp = p2 - p1;
  const dl = (lon2 - lon1) * DEG;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The region a point falls in, the closest fit (distance over radius) where several do; null over
 * open water and unmapped land (atlas `region_at`). */
export function regionAt(lat: number, lon: number): Region | null {
  let best: Region | null = null;
  let bestFit = Infinity;
  for (const region of REGIONS) {
    const fit = distanceKm(lat, lon, region.lat, region.lon) / region.km;
    if (fit <= 1 && fit < bestFit) {
      best = region;
      bestFit = fit;
    }
  }
  return best;
}

/** A region as a tag carries it (a fresh copy: tags are handed to callers). */
export function regionRef(region: Region): RegionRef {
  return { id: region.id, name: region.name, syms: [...region.syms], why: region.why };
}
